import * as React from "react";
import { AiFillInfoCircle } from "react-icons/ai";
import { connect } from "common/connect";
import StoreKeys from "common/storeKeys";
import Store from "common/store";
import PicksHelper from "common/picksHelper";
import EventHelper from "common/helpers/eventHelper/eventHelper";
import ModalHelper from "common/helpers/modalHelper";
import UserManager from "common/userManager";
import AccountRequired from "common/components/Modals/AccountRequired/AccountRequired";
import CustomModal from "common/components/CustomModal/CustomModal";
import {
	ModalPanel,
	ModalHeader,
	ModalBody,
} from "common/components/CustomModal/Modal/ModalFrame";
import FatButton from "common/components/FatButton/FatButton";
import ScorePillModal from "common/components/Modals/ScorePillModal/ScorePillModal";
import PickService from "services/PickService";
import {
	ChampionPickStatus,
	getChampionPickStatus,
} from "common/helpers/championPickHelper";
import "./EventOverview.scss";

const propKeys = {
	eventData: StoreKeys.EVENT.EVENTDATA,
	format: StoreKeys.EVENT.FORMAT,
	championPick: StoreKeys.EVENT.CHAMPION_PICK,
	officialBracketPicks: StoreKeys.EVENT.OFFICIAL.BRACKET_PICKS,
	officialGroupPicks: StoreKeys.EVENT.OFFICIAL.GROUP_PICKS,
	bracketUser: StoreKeys.EVENT.BRACKET_USER,
	score: StoreKeys.EVENT.SCORE,
};

const statusLabels = {
	[ChampionPickStatus.LockedIn]: { text: "Locked In", className: "locked-in" },
	[ChampionPickStatus.Champion]: { text: "Champion", className: "champion" },
	[ChampionPickStatus.Missed]: { text: "Missed", className: "missed" },
	[ChampionPickStatus.Eliminated]: {
		text: "Eliminated",
		className: "eliminated",
	},
};

function ConfirmChampionModal({ team, points, onConfirm }) {
	return (
		<CustomModal>
			<ModalPanel>
				<ModalHeader onClose={() => ModalHelper.closeModal()}>
					Going All The Way
				</ModalHeader>
				<ModalBody>
					<div className="champion-confirm">
						<img src={team.logoUrl} alt={team.name} />
						<p>
							Pick <label className="pickstop-color">{team.name}</label> to
							win the event
							{points > 0 && (
								<>
									{" "}
									(
									<label className="pickstop-color">
										+{points} points
									</label>{" "}
									if correct)
								</>
							)}
							. You can change this until the event starts.
						</p>
					</div>
					<div style={{ display: "flex", justifyContent: "flex-end" }}>
						<FatButton
							text="Confirm"
							primary
							onClick={() => onConfirm(team)}
						/>
					</div>
				</ModalBody>
			</ModalPanel>
		</CustomModal>
	);
}

function dedupeTeams(teams) {
	const byId = new Map();
	for (const team of teams ?? []) {
		if (!byId.has(team.id)) {
			byId.set(team.id, team);
		}
	}

	return [...byId.values()].sort(
		(a, b) =>
			(a.seed ?? Number.MAX_SAFE_INTEGER) -
				(b.seed ?? Number.MAX_SAFE_INTEGER) ||
			(a.name ?? "").localeCompare(b.name ?? "")
	);
}

function EventOverviewC({
	eventData,
	format,
	championPick,
	officialBracketPicks,
	officialGroupPicks,
	bracketUser,
	score,
}) {
	const picksHelper = new PicksHelper(eventData);
	const started = picksHelper.isStarted();
	const viewingOther = !!bracketUser;

	// Ticks the lock countdown once a second.
	const [, setTick] = React.useState(0);
	React.useEffect(() => {
		if (started) {
			return undefined;
		}

		const timer = setInterval(() => setTick((tick) => tick + 1), 1000);
		return () => clearInterval(timer);
	}, [started]);

	if (!eventData || !format) {
		return <></>;
	}

	const points = Number(format.championPick?.points) || 0;
	const teams = dedupeTeams(eventData.teams);
	const pickedTeam = teams.find((t) => t.id === championPick?.teamId);
	const scoringGroups = format.scoringBreakdown?.groups;

	const openScoring = () => {
		ModalHelper.openModal(<ScorePillModal />, null, {
			groups: scoringGroups,
			bracketScore: score,
		});
	};

	const confirmPick = async (team) => {
		try {
			await PickService.saveChampionPick(eventData.id, team.id);
			Store.set(StoreKeys.EVENT.CHAMPION_PICK, {
				...(championPick ?? {}),
				teamId: team.id,
			});
			ModalHelper.closeModal();
		} catch {
			// axiosWrapper already surfaced the error modal.
		}
	};

	const pickTeam = (team) => {
		if (started || viewingOther) {
			return;
		}

		if (!UserManager.isLoggedIn()) {
			ModalHelper.openModal(<AccountRequired />);
			return;
		}

		ModalHelper.openModal(
			<ConfirmChampionModal
				team={team}
				points={points}
				onConfirm={confirmPick}
			/>
		);
	};

	const status = getChampionPickStatus({
		format,
		eventData,
		officialBracketPicks,
		officialGroupPicks,
		championPick,
	});
	const statusLabel = statusLabels[status];

	const stageRows = (format.stages ?? []).map((stage, i) => {
		const startTime = eventData.eventStages?.[i]?.startTime;
		const dateLabel = startTime
			? new Intl.DateTimeFormat("en", {
					month: "short",
					day: "2-digit",
			  }).format(picksHelper.convertUtcToLocalTime(new Date(startTime)))
			: "TBD";
		return (
			<div className="stage-row" key={i}>
				<label>{stage.name}</label>
				<span>{dateLabel}</span>
			</div>
		);
	});

	const currentPick = pickedTeam && (
		<div className="current-pick">
			<img src={pickedTeam.logoUrl} alt={pickedTeam.name} />
			<label>{pickedTeam.name}</label>
			{started && statusLabel && (
				<span className={`pick-status ${statusLabel.className}`}>
					{statusLabel.text}
				</span>
			)}
		</div>
	);

	return (
		<div className="event-overview">
			<div className="overview-header">
				<div
					className="overview-hero"
					style={{
						backgroundImage: `url("${EventHelper.getEventImageUrl(
							eventData
						)}")`,
					}}
				/>
				<div className="overview-title">
					<h1>{eventData.nameOverride ?? eventData.name}</h1>
					<div className="event-dates">
						{picksHelper.getEventDateRange()}
					</div>
					{viewingOther && (
						<div className="viewing-user">
							{bracketUser.username + "'s picks"}
						</div>
					)}
				</div>
			</div>

			<div className="overview-columns">
				<div className="overview-panel schedule-panel">
					<h2>Schedule</h2>
					{stageRows}
				</div>

				<div className="overview-panel champion-panel">
					<div className="panel-heading">
						<h2>Going All The Way</h2>
						{scoringGroups?.length > 0 && (
							<span className="scoring-link" onClick={openScoring}>
								<AiFillInfoCircle />
								<span>Scoring</span>
							</span>
						)}
					</div>
					<p>
						Pick the team that wins the whole event.
						{points > 0 && (
							<>
								{" "}
								If you're right, you get{" "}
								<label className="pickstop-color">
									+{points} points
								</label>{" "}
								on top of what your stage picks earn.
							</>
						)}
					</p>
					{currentPick ??
						(viewingOther && !started ? (
							<p className="pick-hint">
								{`${bracketUser.username ?? "This user"}'s pick is hidden until the event starts.`}
							</p>
						) : started ? (
							<p className="pick-hint">No champion pick.</p>
						) : (
							<p className="pick-hint">Pick a team below.</p>
						))}
					{!started && (
						<div className="countdown">
							<span>Locks in</span>
							<label>
								{picksHelper.getTimeTill(picksHelper.getStartTime())}
							</label>
						</div>
					)}
				</div>
			</div>

			<div className="overview-panel teams-panel">
				<h2>Teams</h2>
				{teams.length === 0 && (
					<p className="pick-hint">No teams added yet.</p>
				)}
				<div className="teams-grid">
					{teams.map((team) => (
						<div
							className={`team-card${
								team.id === championPick?.teamId ? " picked" : ""
							}${!started && !viewingOther ? " clickable" : ""}`}
							onClick={() => pickTeam(team)}
							key={team.id}
						>
							{!!team.seed && <span className="seed">#{team.seed}</span>}
							<img src={team.logoUrl} alt={team.name} />
							<label>{team.name}</label>
						</div>
					))}
				</div>
			</div>
		</div>
	);
}

export default function EventOverview(props) {
	return connect(<EventOverviewC />, propKeys, props);
}
